<script setup lang="ts">
import { useToggleStore } from '@/store/toggle/toggle'

import { CategoryPage } from '@/types/api/api-v3/category'
import { Tag } from '@/types/api/api-v3/product'
import { Product } from '@/types/api/api-v3/product'
import { Factoid } from '@/types/api/api-v3/slider'

import CategoryFilters from '@/components/Category/CategoryFilters.vue'
import CategoryProductList from '@/components/Category/CategoryProductList.vue'

type Filters = Record<string, number>

type Props = {
  currentCategory: CategoryPage
  isFilterActive?: boolean
  isLoading?: boolean
  templateFactoid?: Factoid
}

const props = withDefaults(defineProps<Props>(), {
  isFilterActive: true,
  templateFactoid: null,
})

const { $metrics } = useNuxtApp()

const { replace } = useRouter()
const { query } = useRoute()

const { newProductCardEnabled } = useToggleStore()

// Флаг, для понимания, включен ли какой- либо фильтр
const isFiltersActivated = ref(false)
// Список примененных фильтров
const selectedFilters = ref<Filters>({})
// Список возможных фильтров на основе отфильтрованных товаров и примененных фильтров
const possibleFilters = ref<Filters>({})

// В зависимости от версии API выбираем поле тегов
const tagField = newProductCardEnabled ? 'main_tags' : 'tags'

const products = computed(() => {
  return !isFiltersActivated.value
    ? props?.currentCategory?.products
    : filteredProducts.value
})

const filteredProducts = computed(() => {
  if (!isFiltersActivated.value) return

  return getFilteredProducts(
    props?.currentCategory?.products,
    selectedFilters.value,
  )
})

// Получение продуктов по примененным фильтрам
const getFilteredProducts = (products: Product[], filters: Filters) => {
  // если нет активных фильтров возвращаем все продукты
  if (!Object.keys(filters).length) return products

  return products.filter((product: Product) => {
    // если тегов нет, отбрасываем продукт
    if (!product[tagField].length) return false
    // счетчик пересечений
    let intersections = 0
    // ищем пересечения
    for (const tag of product[tagField]) {
      if (filters[tag.slug]) intersections++
    }

    // Проверяем кол-во пересечений с кол-во активных фильтров
    return intersections === Object.keys(filters).length
  })
}

// Поиск возможных фильтров, на основе текущих примененных фильтров и отфильтрованных продуктов
const getPossibleFilters = (
  filteredProducts: Product[] | undefined,
  selectedFilters: Filters,
): Filters => {
  if (!filteredProducts) return {}

  const possibleFilters: Filters = {}
  // Получаем массив всех тегов (включая дубликаты)
  const productTags = filteredProducts.flatMap((product) => {
    return product[tagField]
  })

  // Проходимся по тегам и сохраняем возможные фильтры, исключая дубликаты и текущие примененные фильтры
  for (const tag of productTags) {
    if (tag.slug in selectedFilters || tag.slug in possibleFilters) continue

    possibleFilters[tag.slug] = 1
  }

  return possibleFilters
}

const setActiveFilter = (filter: Tag | Partial<Tag>) => {
  // Если нет slug-а у фильтра - выходим
  if (!filter.slug) return

  // Ставим флаг активированых фильтров
  isFiltersActivated.value = true

  // Складываем в объект ключом slug фильтра
  selectedFilters.value[filter.slug] = 1

  setPossibleFilters()

  $metrics?.eventSendGoal({ ym: 'add_filter' })
}

const deactivateFilter = (filter: Tag) => {
  // Если нет slug-а у фильтра - выходим
  if (!filter.slug) return

  // Удаляем ключ у активных фильтров
  delete selectedFilters.value[filter.slug]

  // Если текущее состояние фильтров пустое - очищаем возможные фильтры
  if (Object.keys(selectedFilters.value).length === 0) {
    resetAllFilters()

    return
  }

  setPossibleFilters()
}

/*
  Расчитываем возможные фильтры (Возможными фильтрами называем те фильтры,
  которые не являются активными, но все еще могут быть активированы, т.к попадают под пересечения фильтров продуктов)
*/
const setPossibleFilters = () => {
  possibleFilters.value = getPossibleFilters(
    filteredProducts.value,
    selectedFilters.value,
  )
}

const resetAllFilters = () => {
  isFiltersActivated.value = false
  selectedFilters.value = {}
  possibleFilters.value = {}
}

/*
  Проверем query на наличие переданных фильтров
  Если есть - устанавливаем необходимые фильтры и очищаем query
*/
onMounted(() => {
  if (Object.keys(query).length && query.slug) {
    setActiveFilter({ slug: typeof query.slug === 'string' ? query.slug : '' })

    replace({ query: {} })
  }
})
</script>

<template>
  <category-filters
    v-if="currentCategory.tags?.length && isFilterActive"
    :filters="currentCategory.tags"
    :is-filters-activated="isFiltersActivated"
    :selected-filters="selectedFilters"
    :possible-filters="possibleFilters"
    @set-active-filter="setActiveFilter"
    @deactivate-filter="deactivateFilter"
    @reset-all-filters="resetAllFilters"
  />

  <category-product-list
    :current-category="currentCategory"
    :filtered-products="products"
    :template-factoid="templateFactoid"
    :is-loading="isLoading"
  />
</template>
